import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff } from 'lucide-react';
import Logo from '../components/Logo';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Enter email & password'); return; }
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate('/home', { replace: true });
    } catch (err) {
      setError(err?.message ?? 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-light-gradient flex items-center justify-center p-6" data-testid="login-page">
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <Logo size="xl" theme="light" className="mb-6" />
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight" data-testid="login-title">Welcome back</h1>
          <p className="text-gray-500 mt-2">Sign in to your workspace.</p>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="space-y-5">
          {error && (
            <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="login-email" className="block text-gray-900 text-sm font-bold mb-2 tracking-wide">Email</label>
            <input
              id="login-email"
              data-testid="login-email-input"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full h-14 px-4 rounded-xl border border-gray-200 bg-white text-gray-900 text-base placeholder:text-gray-400 focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 outline-none transition-all"
            />
          </div>

          <div>
            <label htmlFor="login-password" className="block text-gray-900 text-sm font-bold mb-2 tracking-wide">Password</label>
            <div className="relative">
              <input
                id="login-password"
                data-testid="login-password-input"
                type={showPass ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full h-14 px-4 pr-12 rounded-xl border border-gray-200 bg-white text-gray-900 text-base placeholder:text-gray-400 focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 outline-none transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPass(v => !v)}
                data-testid="login-toggle-password"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600"
                aria-label={showPass ? 'Hide password' : 'Show password'}
              >
                {showPass ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full h-14 rounded-xl bg-gradient-to-r from-brand-purple via-brand-purple-soft to-purple-700 text-white font-bold text-base shadow-accent hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
            ) : 'Sign in'}
          </button>
        </form>

        <p className="text-center mt-6 text-gray-500 text-sm">
          No workspace yet?{' '}
          <Link to="/signup" data-testid="go-signup-link" className="text-brand-purple font-bold hover:underline">
            Create one
          </Link>
        </p>

        <div className="text-center mt-4">
          <button
            data-testid="ho-platform-login-btn"
            onClick={() => { window.location.href = '/admin/'; }}
            className="px-5 py-2 rounded-[14px] border border-gray-300/70 text-gray-400 text-sm font-medium hover:border-gray-400 hover:text-gray-500 cursor-pointer transition-colors"
          >
            HO Platform Login
          </button>
        </div>
      </div>
    </div>
  );
}
